import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clientesAPI } from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Users,
  Phone,
  Mail
} from 'lucide-react';
import Swal from 'sweetalert2';

export const Clientes = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  usePageTitle('Clientes');  const navigate = useNavigate();

  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    try {
      const response = await clientesAPI.getAll();
      setClientes(response.data);
    } catch (error) {
      console.error('Error fetching clientes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível carregar os clientes.',
        confirmButtonColor: '#0D9488'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (cliente) => {
    const result = await Swal.fire({
      title: 'Eliminar Cliente?',
      text: `Tem a certeza que deseja eliminar "${cliente.nome}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#64748B',
      confirmButtonText: 'Sim, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await clientesAPI.delete(cliente.id);
        setClientes(clientes.filter(c => c.id !== cliente.id));
        Swal.fire({
          icon: 'success',
          title: 'Eliminado!',
          text: 'Cliente removido com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error deleting cliente:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Não foi possível eliminar o cliente.',
          confirmButtonColor: '#0D9488'
        });
      }
    }
  };

  const filteredClientes = clientes.filter(cliente =>
    cliente.nome?.toLowerCase().includes(search.toLowerCase()) ||
    cliente.email?.toLowerCase().includes(search.toLowerCase()) ||
    cliente.nif?.includes(search) ||
    cliente.id_especial?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="clientes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Clientes</h1>
          <p className="text-slate-500 mt-1">Gestão de clientes da farmácia</p>
        </div>
        <Link to="/clientes/novo">
          <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-cliente-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Cliente
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Pesquisar por nome, email, NIF ou ID especial..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-clientes-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users className="h-5 w-5 text-amber-600" />
            Lista de Clientes ({filteredClientes.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">A carregar...</div>
          ) : filteredClientes.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Users className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p>{search ? 'Nenhum cliente encontrado' : 'Nenhum cliente registado'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Nome</TableHead>
                    <TableHead className="font-semibold text-slate-700">Contacto</TableHead>
                    <TableHead className="font-semibold text-slate-700">Localidade</TableHead>
                    <TableHead className="font-semibold text-slate-700">NIF</TableHead>
                    <TableHead className="font-semibold text-slate-700">ID Especial</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClientes.map((cliente) => (
                    <TableRow key={cliente.id} className="hover:bg-slate-50" data-testid={`cliente-row-${cliente.id}`}>
                      <TableCell className="font-medium text-slate-900">
                        {cliente.nome}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {cliente.telemovel && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Phone className="h-3 w-3" />
                              {cliente.telemovel}
                            </div>
                          )}
                          {cliente.email && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Mail className="h-3 w-3" />
                              {cliente.email}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {cliente.cidade || '-'}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {cliente.nif || '-'}
                      </TableCell>
                      <TableCell>
                        {cliente.id_especial ? (
                          <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded-md text-sm font-medium">
                            {cliente.id_especial}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/clientes/${cliente.id}/editar`)}
                            data-testid={`edit-cliente-${cliente.id}`}
                          >
                            <Edit className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(cliente)}
                            data-testid={`delete-cliente-${cliente.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
